import React, { useState, useEffect } from 'react';
import { houseTargets as mockHouseTargets } from '../mockData';
import { Home, Target } from 'lucide-react';
import axios from 'axios';
import { updatePageSEO } from '../utils/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HouseTarget = () => {
  const [houseTargets, setHouseTargets] = useState(mockHouseTargets);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Update SEO for House Target page
    updatePageSEO({
      path: '/house-target',
      title: 'Morning Sunday Teer House Target | House Ending Prediction',
      description: 'Check Morning Sunday Teer House Target and house ending predictions. Get accurate house number targets for first and second round.',
      robots: 'index, follow'
    });
    
    fetchHouseTargets();
  }, []);

  const fetchHouseTargets = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/house-targets/today`);
      setHouseTargets(response.data);
    } catch (err) {
      console.error('Error fetching house targets:', err);
      // Keep using mock data if API fails
      setHouseTargets(mockHouseTargets);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Home className="w-12 h-12 text-coral-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Shillong Morning Teer House Target
          </h1>
          <p className="text-lg text-gray-600">
            House predictions and target numbers for today
          </p>
        </div>

        {/* House Targets Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {houseTargets.map((target, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              <div className="bg-gradient-to-r from-coral-600 to-coral-700 p-4">
                <h3 className="text-xl font-bold text-white text-center">{target.house}</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="flex items-center mb-3">
                      <Target className="w-5 h-5 text-coral-600 mr-2" />
                      <span className="font-semibold text-gray-900">F/R Target</span>
                    </div>
                    <div className="space-y-2">
                      {target.fr.split('-').map((num, idx) => (
                        <div
                          key={idx}
                          className="bg-gray-100 rounded-lg p-3 text-center"
                        >
                          <span className="text-2xl font-bold text-gray-900">{num}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center mb-3">
                      <Target className="w-5 h-5 text-coral-600 mr-2" />
                      <span className="font-semibold text-gray-900">S/R Target</span>
                    </div>
                    <div className="space-y-2">
                      {target.sr.split('-').map((num, idx) => (
                        <div
                          key={idx}
                          className="bg-gray-100 rounded-lg p-3 text-center"
                        >
                          <span className="text-2xl font-bold text-gray-900">{num}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Info Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Understanding House Targets
          </h2>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              House targets are predictions shared by various Teer houses and experienced players. 
              These numbers are based on analysis of previous results, trends, and traditional calculation methods.
            </p>
            <p>
              Different houses may provide different target numbers based on their unique analysis approach. 
              Many followers compare multiple house targets before making their own decisions.
            </p>
            <p className="font-semibold text-coral-600">
              Remember: House targets are predictions only and do not guarantee actual results. 
              Always play responsibly and within your means.
            </p>
          </div>
        </div>

        {/* Warning */}
        <div className="mt-8 bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
          <p className="text-sm text-gray-700">
            <strong>Important Notice:</strong> House targets are provided for informational purposes only. 
            These are predictions and not guaranteed outcomes. Please verify all numbers with official sources 
            and play responsibly.
          </p>
        </div>
      </div>
    </div>
  );
};

export default HouseTarget;
